import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../../services/api";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  chipPrincipal: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
}));

const ClienteEnderecos = ({ clienteId }) => {
  const classes = useStyles();
  const [enderecos, setEnderecos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [enderecoEdit, setEnderecoEdit] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    pais: "Brasil",
    principal: false,
    ativo: true,
  });

  const estados = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
    "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
    "RS", "RO", "RR", "SC", "SP", "SE", "TO",
  ];

  useEffect(() => {
    if (clienteId) {
      loadEnderecos();
    }
  }, [clienteId]);

  const loadEnderecos = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clientes/${clienteId}/enderecos`);
      setEnderecos(data);
    } catch (error) {
      console.error("Erro ao carregar endereços:", error);
      toast.error("Erro ao carregar endereços");
    } finally {
      setLoading(false);
    }
  };

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, "");
    if (cepLimpo.length !== 8) return;

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cepLimpo}/json/`
      );
      const data = await response.json();

      if (!data.erro) {
        setFormData({
          ...formData,
          cep: cep,
          endereco: data.logradouro || formData.endereco,
          bairro: data.bairro || formData.bairro,
          cidade: data.localidade || formData.cidade,
          estado: data.uf || formData.estado,
        });
      } else {
        toast.error("CEP não encontrado");
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      toast.error("Erro ao buscar CEP");
    }
  };

  const handleOpenModal = (endereco = null) => {
    if (endereco) {
      setEnderecoEdit(endereco);
      setFormData(endereco);
    } else {
      setEnderecoEdit(null);
      setFormData({
        nome: "",
        cep: "",
        endereco: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        estado: "",
        pais: "Brasil",
        principal: false,
        ativo: true,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEnderecoEdit(null);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" 
      ? event.target.checked 
      : event.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    try {
      if (!formData.nome || !formData.cidade || !formData.estado) {
        toast.error("Preencha os campos obrigatórios: Nome, Cidade e Estado");
        return;
      }

      if (enderecoEdit) {
        await api.put(
          `/clientes/${clienteId}/enderecos/${enderecoEdit.id}`,
          formData
        );
        toast.success("Endereço atualizado com sucesso!");
      } else {
        await api.post(`/clientes/${clienteId}/enderecos`, formData);
        toast.success("Endereço cadastrado com sucesso!");
      }

      handleCloseModal();
      loadEnderecos();
    } catch (error) {
      console.error("Erro ao salvar endereço:", error);
      const errorMessage = error.response?.data?.error || "Erro ao salvar endereço";
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este endereço?")) {
      return;
    }

    try {
      await api.delete(`/clientes/${clienteId}/enderecos/${id}`);
      toast.success("Endereço excluído com sucesso!");
      loadEnderecos();
    } catch (error) {
      console.error("Erro ao excluir endereço:", error);
      toast.error("Erro ao excluir endereço");
    }
  };

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h6">
          <LocationIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
          Endereços Cadastrados
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          Novo Endereço
        </Button>
      </Box>

      <Paper>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CEP</TableCell>
              <TableCell>Endereço</TableCell>
              <TableCell>Bairro</TableCell>
              <TableCell>Cidade/UF</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enderecos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum endereço cadastrado
                </TableCell>
              </TableRow>
            ) : (
              enderecos.map((endereco) => (
                <TableRow key={endereco.id}>
                  <TableCell>
                    <strong>{endereco.nome}</strong>
                  </TableCell>
                  <TableCell>{endereco.cep}</TableCell>
                  <TableCell>
                    {endereco.endereco}
                    {endereco.numero && `, ${endereco.numero}`}
                    {endereco.complemento && ` - ${endereco.complemento}`}
                  </TableCell>
                  <TableCell>{endereco.bairro}</TableCell>
                  <TableCell>
                    {endereco.cidade}/{endereco.estado}
                  </TableCell>
                  <TableCell>
                    {endereco.principal && (
                      <Chip
                        label="Principal"
                        size="small"
                        className={classes.chipPrincipal}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal(endereco)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(endereco.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal de Cadastro/Edição */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {enderecoEdit ? "Editar Endereço" : "Novo Endereço"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Nome do Endereço *"
                fullWidth
                value={formData.nome}
                onChange={handleChange("nome")}
                required
                placeholder="Ex: Matriz, Filial São Paulo, Depósito, etc."
                helperText="Identificação para este endereço"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="CEP"
                fullWidth
                value={formData.cep}
                onChange={(e) => {
                  handleChange("cep")(e);
                  if (e.target.value.replace(/\D/g, "").length === 8) {
                    buscarCEP(e.target.value);
                  }
                }}
                placeholder="00000-000"
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                label="Logradouro (Rua, Avenida, etc.)"
                fullWidth
                value={formData.endereco}
                onChange={handleChange("endereco")}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="Número"
                fullWidth
                value={formData.numero}
                onChange={handleChange("numero")}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="Complemento"
                fullWidth
                value={formData.complemento}
                onChange={handleChange("complemento")}
                placeholder="Sala, Andar, etc."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Bairro"
                fullWidth
                value={formData.bairro}
                onChange={handleChange("bairro")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Cidade *"
                fullWidth
                value={formData.cidade}
                onChange={handleChange("cidade")}
                required
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth required>
                <InputLabel>UF *</InputLabel>
                <Select
                  value={formData.estado}
                  onChange={handleChange("estado")}
                >
                  {estados.map((uf) => (
                    <MenuItem key={uf} value={uf}>
                      {uf}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="País"
                fullWidth
                value={formData.pais}
                onChange={handleChange("pais")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.principal}
                    onChange={handleChange("principal")}
                    color="primary"
                  />
                }
                label="Endereço Principal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClienteEnderecos;
